// @ts-nocheck
import styled from '@emotion/styled';
import {
  Chip,
  Grid,
  Avatar,
  Typography,
  Box,
  Popover as MuiPopover,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ArtifactIcon } from '../../../../assets/Icons/ArtifactsIcon.svg';
import { ReactComponent as DocumentIcon } from '../../../../assets/Icons/ChatIcons/documents.svg';
import { ReactComponent as AttributeIcon } from '../../../../assets/Icons/ChatIcons/attribute.svg';
import { ReactComponent as ModelIcon } from '../../../../assets/Icons/ChatIcons/model.svg';
import { ReactComponent as GitPullRequest } from '../../../../assets/images/ModelDetails/GitPullRequest.svg';
import { ENTITY_TYPE } from '../../../../const/CommonConst';
import useDocumentInventory from '../../../../hooks/ModelInventory/useDocumentInventory';
import {
  downloadBlobResponse,
  getEntityTypeKey,
  getEntityTypes,
  getLabelPathFromLabelConfig,
  isValueEmpty,
} from '../../../../utils/Utils';
import DocumentPreviewModal from '../../../DocumentInventory/DocumentPreviewModal';
import { DEFAULT_ROUTE_CONFIG } from '../../../../const/defaultRoutesConfig';
import swalToast from '../../../../components/Common/swalToast';

const INLINE_BLOCK = 'inline-block';
const ARTIFACT_MENTION_ICON_SIZE = '16px';

export const CommonMentionStyledChip = styled(Chip)(({ theme }) => {
  return {
    padding: '4px 8px',
    backgroundColor: `${theme?.palette?.mentionsInChatEntityColor?.background} !important`,
    borderRadius: '4px',
    minHeight: '22px',
    height: '22px',
    gap: '4px',
    '& .MuiChip-label': {
      padding: 0,
      fontSize: '12px',
      // lineHeight: '14px',
      fontWeight: 400,
      color: `${theme?.palette?.mentionsInChatEntityColor?.textColor}`,
    },
    '& .MuiChip-icon': {
      margin: 0,
      color: `${theme?.palette?.mentionsInChatEntityColor?.textColor}`,
    },
  };
});

CommonMentionStyledChip.propTypes = {
  mentionType: PropTypes.oneOf([
    'username',
    'document',
    'artifact',
    'attribute',
  ]),
};

export const MentionComponent = (props) => {
  const { decoratedText, mention, children } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);
  const handleShow = useCallback(
    (e) => {
      setAnchorEl(e.currentTarget);
    },
    [setAnchorEl]
  );
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <>
      <span style={{ display: 'none' }}>{children}</span>
      <CommonMentionStyledChip
        mentionType="username"
        label={decoratedText}
        sx={{
          '& .MuiChip-avatar': {
            color: (theme) => `${theme?.palette?.other?.white}`,
            fontSize: '17px',
            fontWeight: '600',
          },
        }}
        onClick={handleShow}
      />
      <MuiPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Grid
          container
          alignItems="center"
          sx={{
            padding: '16px',
          }}
        >
          <Grid
            xs={4}
            item
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar
              sx={{
                fontSize: '24px',
                textAlign: 'center',
                lineHeight: '50px',
              }}
            >
              {mention?.name?.charAt(0)}
            </Avatar>
          </Grid>
          <Grid xs={8} item>
            <Typography sx={{ display: INLINE_BLOCK, padding: '6px 14px' }}>
              {mention?.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ display: INLINE_BLOCK, padding: '6px 14px' }}
            >
              {/* eslint-disable-next-line react/prop-types */}
              {mention?.designation}
            </Typography>
          </Grid>
        </Grid>
      </MuiPopover>
    </>
  );
};

MentionComponent.propTypes = {
  decoratedText: PropTypes.string.isRequired,
  mention: PropTypes.shape({
    name: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export const redirectToModelArtifact = (mention, history) => {
  const currentUniqueId = mention?.Unique_id;

  const entityTypeKey = getEntityTypeKey(
    mention?.Entity_type,
    mention?.request_type
  );

  const dynamicEntityPath = getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
  );

  const dynamicEntityLabel = getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
    'label',
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
  );

  history(
    {
      pathname: `/${dynamicEntityPath}/${currentUniqueId}`,
    },
    {
      state: [
        {
          label: dynamicEntityLabel,
          path: `/${dynamicEntityPath}`,
        },
        {
          label: currentUniqueId,
          path: `/${dynamicEntityPath}/${currentUniqueId}`,
          type:
            ENTITY_TYPE[
              getEntityTypes(mention?.Entity_type, mention?.request_type)
            ] || mention?.Entity_type,
        },
      ],
    }
  );
};

export const ModelMentionComponent = (props) => {
  const history = useNavigate();
  const theme = useTheme();
  const { mention, children } = props;

  const handleClickModelMentionComponent = useCallback(
    () => redirectToModelArtifact(mention, history),
    [mention, history, redirectToModelArtifact]
  );

  return (
    <span title={`${mention?.Unique_id} ${mention?.Name?.trim()}`}>
      <span style={{ display: 'none' }}>{children}</span>
      <CommonMentionStyledChip
        mentionType="model"
        label={`${mention?.Unique_id} ${mention?.Name}`}
        icon={
          <ModelIcon
            style={{ color: theme?.palette?.mentionsInChatEntityColor?.model }}
          />
        }
        onClick={handleClickModelMentionComponent}
      />
    </span>
  );
};

ModelMentionComponent.propTypes = {
  mention: PropTypes.shape({
    Unique_id: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export const ArtifactMentionComponent = (props) => {
  const theme = useTheme();
  const history = useNavigate();

  const { mention, children } = props;

  const handleClickArtifactMentionComponent = useCallback(
    () => redirectToModelArtifact(mention, history),
    [redirectToModelArtifact, history, mention]
  );

  return (
    <span title={`${mention?.Unique_id} ${mention?.Name?.trim()}`}>
      <span style={{ display: 'none' }}>{children}</span>
      <CommonMentionStyledChip
        mentionType="artifact"
        label={`${mention?.Unique_id} ${mention?.Name}`}
        icon={
          mention?.request_type === ENTITY_TYPE.ModelRequest ? (
            <GitPullRequest
              style={{
                width: ARTIFACT_MENTION_ICON_SIZE,
                height: ARTIFACT_MENTION_ICON_SIZE,
                flexShrink: 0,
                display: 'block',
                color: theme?.palette?.mentionsInChatEntityColor?.artifact,
              }}
            />
          ) : (
            <ArtifactIcon
              style={{
                width: ARTIFACT_MENTION_ICON_SIZE,
                height: ARTIFACT_MENTION_ICON_SIZE,
                flexShrink: 0,
                display: 'block',
                color: theme?.palette?.mentionsInChatEntityColor?.artifact,
              }}
            />
          )
        }
        onClick={handleClickArtifactMentionComponent}
      />
    </span>
  );
};

ArtifactMentionComponent.propTypes = {
  mention: PropTypes.shape({
    Unique_id: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
    request_type: PropTypes.string,
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export const DocumentMentionComponent = ({ props, fetchDocumentDetails }) => {
  const theme = useTheme();

  const { mention, children } = props;
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [selectedDocumentForEdit, setSelectedDocumentForEdit] = useState({});
  const {
    getDocumentMarkdown,
    getVersionDetails,
    saveAsNewVersion,
    updateDocument,
    downloadDocument,
  } = useDocumentInventory();

  const handlePDFOpen = (doc, type) => {
    const document = doc;
    downloadDocument(
      {
        document_id: document?.document_id,
        entity_id: document?.entity_id,
        entity_type: document?.entity_type,
        export_type: type,
        commit_id:
          document?.versions?.length > 0
            ? `${document?.versions[document.versions.length - 1]?.version}`
            : null,
      },
      {
        responseType: type === 'onedrive_docx' ? 'json' : 'blob',
      }
    ).then((response) => {
      if (response) {
        if (type === 'onedrive_docx') {
          window.open(response?.data?.Data, '_blank');
        } else {
          try {
            downloadBlobResponse(
              response,
              `${document?.document_name}.${type}`
            );
          } catch (e) {
            swalToast({
              icon: 'error',
              title: `Error occurred at time of downloading file. ${e}`,
            });
          }
        }
      }
    });
  };

  const handleOpenDocument = useCallback(() => {
    fetchDocumentDetails({
      entity_type: mention?.Entity_type,
      entity_id: mention?.Entity_id,
      document_id: mention?.Id,
    }).then((res) => {
      if (res?.data) {
        const doc = res?.data[0];
        if (isValueEmpty(doc) || isValueEmpty(doc?.file_type)) {
          swalToast({
            icon: 'error',
            title: 'Document not found or it is deleted.',
          });
        } else {
          setOpenDocumentModal(true);
          setSelectedDocumentForEdit(doc);
        }
      }
    });
  }, [
    fetchDocumentDetails,
    mention,
    handlePDFOpen,
    setOpenDocumentModal,
    setSelectedDocumentForEdit,
    isValueEmpty,
    swalToast,
  ]);

  const handleCloseDocumentPreviewModal = useCallback(() => {
    setOpenDocumentModal(false);
  }, [setOpenDocumentModal]);

  return (
    <span title={`${mention?.Name?.trim()}`}>
      <span style={{ display: 'none' }}>{children}</span>
      <CommonMentionStyledChip
        mentionType="document"
        label={`${mention?.Name?.trim()}`}
        icon={
          <DocumentIcon
            style={{
              color: theme?.palette?.mentionsInChatEntityColor?.documentNode,
            }}
          />
        }
        onClick={handleOpenDocument}
      />
      {openDocumentModal ? (
        <DocumentPreviewModal
          open={openDocumentModal}
          getDocumentMarkdown={getDocumentMarkdown}
          getVersionDetails={getVersionDetails}
          updateDocument={updateDocument}
          saveAsNewVersion={saveAsNewVersion}
          selectedDocumentForEdit={selectedDocumentForEdit}
          handleClose={handleCloseDocumentPreviewModal}
          setSelectedDocumentForEdit={setSelectedDocumentForEdit}
        />
      ) : null}
    </span>
  );
};

DocumentMentionComponent.propTypes = {
  mention: PropTypes.shape({
    Name: PropTypes.string.isRequired,
    Id: PropTypes.number.isRequired,
    Entity_type: PropTypes.string.isRequired,
    Entity_id: PropTypes.number.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
  fetchDocumentDetails: PropTypes.func.isRequired,
  props: PropTypes.node.isRequired,
};

const ATTRIBUTE_EMPTY_VALUE_LABEL = 'No value set';

const isAttributeMentionValueEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isAttributeMentionValueEmpty);
  }
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const extractAttributeMentionValue = (responseData) => {
  if (responseData === null || responseData === undefined) return null;
  if (Array.isArray(responseData)) {
    return responseData.length > 0 ? responseData : null;
  }
  if (typeof responseData !== 'object') return responseData;
  if (Object.hasOwn(responseData, 'value')) return responseData.value;

  const entries = Object.entries(responseData);
  if (entries.length === 0) return null;

  return entries[0][1];
};

const formatAttributeMentionValue = (value) => {
  if (isAttributeMentionValueEmpty(value)) return ATTRIBUTE_EMPTY_VALUE_LABEL;
  if (Array.isArray(value)) {
    const formattedItems = value
      .map(formatAttributeMentionValue)
      .filter((item) => item !== ATTRIBUTE_EMPTY_VALUE_LABEL);

    return formattedItems.length > 0
      ? formattedItems.join(', ')
      : ATTRIBUTE_EMPTY_VALUE_LABEL;
  }
  if (typeof value === 'object') {
    const preferredValue =
      value.label ??
      value.name ??
      value.Name ??
      value.value ??
      value.Id ??
      value.id;

    if (!isAttributeMentionValueEmpty(preferredValue)) {
      return formatAttributeMentionValue(preferredValue);
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

export const AttributeMentionComponent = ({ props, getAttributeDetails }) => {
  const { mention, children } = props;
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attributeValue, setAttributeValue] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const formattedAttributeValue = useMemo(
    () => formatAttributeMentionValue(attributeValue),
    [attributeValue]
  );
  const isAttributeValueEmpty = useMemo(
    () => isAttributeMentionValueEmpty(attributeValue),
    [attributeValue]
  );

  const handleShowAttributeInformation = useCallback(
    (e) => {
      handleClick(e);
      setIsLoading(true);
      getAttributeDetails({
        entity_type: mention?.Entity_type,
        entity_id: mention?.Entity_id,
        attribute_id: mention?.Id,
      })
        .then((res) => {
          setAttributeValue(extractAttributeMentionValue(res?.data));
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [mention, getAttributeDetails, setAttributeValue, setIsLoading, handleClick]
  );

  return (
    <span title={`${mention?.Name?.trim()}`}>
      <span style={{ display: 'none' }}>{children}</span>
      <CommonMentionStyledChip
        mentionType={'attribute'}
        label={`${mention?.Name}`}
        icon={
          <AttributeIcon
            style={{
              color:
                theme?.palette?.mentionsInChatEntityColor?.globalAttributes,
            }}
          />
        }
        onClick={handleShowAttributeInformation}
      />
      <MuiPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxWidth: 360,
          },
        }}
      >
        <Box
          sx={{
            minWidth: 220,
            maxWidth: 360,
            p: 1.5,
          }}
        >
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                columnGap: 0.5,
                rowGap: 0.25,
                lineHeight: 1.5,
              }}
            >
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {mention?.Name?.trim()}
              </Typography>
              <Typography component="span" variant="body2">
                :
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  lineHeight: 1.5,
                }}
                title={formattedAttributeValue}
                color={
                  isAttributeValueEmpty ? 'text.secondary' : 'text.primary'
                }
              >
                {formattedAttributeValue}
              </Typography>
            </Box>
          )}
        </Box>
      </MuiPopover>
    </span>
  );
};

AttributeMentionComponent.propTypes = {
  mention: PropTypes.shape({
    Entity_type: PropTypes.string.isRequired,
    Entity_id: PropTypes.number.isRequired,
    Id: PropTypes.number.isRequired,
    Name: PropTypes.number.isRequired,
  }).isRequired,
  children: PropTypes.node.isRequired,
  props: PropTypes.node.isRequired,
  getAttributeDetails: PropTypes.func.isRequired,
};
